import React, { useEffect, useState } from "react";
import { useAgeGate } from "@/context/AgeGateContext";

/**
 * A goofy mascot that lives in the bottom-RIGHT corner and
 * every ~25 seconds runs across to the bottom-LEFT corner and back.
 *
 * Hidden during the hero reveal sequence so it doesn't intrude on the
 * "big reveal" of Layla. Fades back in ~6s after age gate is confirmed.
 */
export default function MuscleMan() {
  // 'right' (idle), 'running-left', 'left' (idle briefly), 'running-right'
  const [phase, setPhase] = useState("right");
  const { verified } = useAgeGate();
  const [visible, setVisible] = useState(false);

  // Stay hidden until ~6s after the user passes the age gate so the hero
  // reveal can play out cleanly.
  useEffect(() => {
    if (!verified) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), 6000);
    return () => clearTimeout(t);
  }, [verified]);

  useEffect(() => {
    let timer;
    const cycle = () => {
      setPhase("running-left");
      timer = setTimeout(() => {
        setPhase("left");
        timer = setTimeout(() => {
          setPhase("running-right");
          timer = setTimeout(() => {
            setPhase("right");
            timer = setTimeout(cycle, 22000 + Math.random() * 10000); // wait 22-32s between laps
          }, 3500); // running right back — 3.5s
        }, 1500); // idle on the left for 1.5s
      }, 3500); // running left — 3.5s
    };
    timer = setTimeout(cycle, 8000); // first lap after 8s
    return () => clearTimeout(timer);
  }, []);

  // Position & flip depending on phase
  const running = phase === "running-left" || phase === "running-right";
  const facingLeft = phase === "running-left" || phase === "left";

  const style = {
    // Bottom offset — raised above the Emergent badge
    bottom: "70px",
    // horizontal behavior via style to get smooth transition
    transition:
      phase === "running-left"
        ? "right 3.5s linear, opacity 700ms ease"
        : phase === "running-right"
        ? "right 3.5s linear, opacity 700ms ease"
        : "right 0s, opacity 700ms ease",
    right: phase === "left" || phase === "running-left" ? "calc(100vw - 60px)" : "12px",
    transform: `scaleX(${facingLeft ? -1 : 1})`,
    pointerEvents: "none",
    opacity: visible ? 1 : 0,
  };

  // Smaller mascot on phones so it doesn't crowd the portrait
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const mmSize = isMobile ? 36 : 48;

  return (
    <div
      className="fixed z-[55] select-none"
      style={style}
      data-testid="muscle-man"
      aria-hidden
    >
      <img
        src="/muscle-man.png"
        alt=""
        width={mmSize}
        height={mmSize}
        className={running ? "animate-mm-bob" : "animate-mm-idle"}
        style={{ display: "block", filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.5))" }}
      />
    </div>
  );
}
